/**
 * AI 分析履歴管理サービス
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from './config';
import {
  AIAnalysisHistory,
  AIAnalysisHistoryInput,
  AIAnalysisHistoryFilters,
  PaginationOptions,
} from '@/types/aiManagement';

/**
 * ページネーション結果の型
 */
export interface PaginatedHistories {
  histories: AIAnalysisHistory[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

/**
 * AI分析履歴管理サービスクラス
 */
export class AIAnalysisHistoryService {
  private readonly COLLECTION_NAME = 'aiAnalysisHistory';

  /**
   * 分析履歴を保存
   */
  async saveHistory(data: AIAnalysisHistoryInput): Promise<string> {
    try {
      const docRef = doc(collection(db, this.COLLECTION_NAME));
      const now = Timestamp.now();

      const history: Omit<AIAnalysisHistory, 'id'> = {
        ...data,
        createdAt: now,
      };

      await setDoc(docRef, history);

      return docRef.id;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`分析履歴の保存に失敗しました: ${error.message}`);
      }
      throw new Error('分析履歴の保存に失敗しました');
    }
  }

  /**
   * 分析履歴一覧を取得（ページネーション対応）
   */
  async getHistories(
    userId: string,
    filters?: AIAnalysisHistoryFilters,
    pagination?: PaginationOptions
  ): Promise<PaginatedHistories> {
    try {
      const constraints: ReturnType<typeof where | typeof orderBy | typeof limit | typeof startAfter>[] = [
        where('userId', '==', userId),
      ];

      // フィルター適用
      if (filters?.status) {
        constraints.push(where('status', '==', filters.status));
      }

      if (filters?.femaleId) {
        constraints.push(where('input.femaleId', '==', filters.femaleId));
      }

      // 日付範囲フィルター
      if (filters?.startDate) {
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.startDate)));
      }

      if (filters?.endDate) {
        // 終了日の翌日0時まで含める
        const endOfDay = new Date(filters.endDate);
        endOfDay.setHours(23, 59, 59, 999);
        constraints.push(where('createdAt', '<=', Timestamp.fromDate(endOfDay)));
      }

      // ソート
      constraints.push(orderBy('createdAt', 'desc'));

      // ページネーション
      const pageLimit = pagination?.limit || 20;
      constraints.push(limit(pageLimit + 1)); // 次ページ存在確認用に+1

      if (pagination?.startAfterDoc) {
        constraints.push(startAfter(pagination.startAfterDoc));
      }

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);

      const docs = snapshot.docs;
      const hasMore = docs.length > pageLimit;
      const resultDocs = hasMore ? docs.slice(0, pageLimit) : docs;

      const histories = resultDocs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as AIAnalysisHistory[];

      return {
        histories,
        lastDoc: resultDocs.length > 0 ? resultDocs[resultDocs.length - 1] : null,
        hasMore,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`分析履歴の取得に失敗しました: ${error.message}`);
      }
      throw new Error('分析履歴の取得に失敗しました');
    }
  }

  /**
   * 分析履歴をIDで取得
   */
  async getHistoryById(id: string): Promise<AIAnalysisHistory | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() } as AIAnalysisHistory;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`分析履歴の取得に失敗しました: ${error.message}`);
      }
      throw new Error('分析履歴の取得に失敗しました');
    }
  }

  /**
   * CSVエクスポート用に全履歴を取得（ページネーションなし）
   */
  async getHistoriesForExport(
    userId: string,
    filters?: Pick<AIAnalysisHistoryFilters, 'startDate' | 'endDate'>
  ): Promise<AIAnalysisHistory[]> {
    try {
      const constraints: ReturnType<typeof where | typeof orderBy>[] = [
        where('userId', '==', userId),
      ];

      // 日付範囲フィルター
      if (filters?.startDate) {
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.startDate)));
      }

      if (filters?.endDate) {
        const endOfDay = new Date(filters.endDate);
        endOfDay.setHours(23, 59, 59, 999);
        constraints.push(where('createdAt', '<=', Timestamp.fromDate(endOfDay)));
      }

      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as AIAnalysisHistory[];
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`エクスポート用履歴の取得に失敗しました: ${error.message}`);
      }
      throw new Error('エクスポート用履歴の取得に失敗しました');
    }
  }

  /**
   * 履歴をCSV形式に変換
   */
  convertToCSV(histories: AIAnalysisHistory[]): string {
    const headers = [
      '実行日時',
      'メス犬名',
      '候補数',
      'ステータス',
      '実行時間(秒)',
      '1位候補',
      '1位スコア',
      '2位候補',
      '2位スコア',
      '3位候補',
      '3位スコア',
    ];

    const rows = histories.map((history) => {
      const date = history.createdAt.toDate().toLocaleString('ja-JP');
      const suggestions = history.output.suggestions || [];

      return [
        date,
        history.input.femaleName,
        history.input.candidateIds.length.toString(),
        history.status === 'success' ? '成功' : 'エラー',
        (history.executionTime / 1000).toFixed(1),
        suggestions[0]?.maleName || '-',
        suggestions[0]?.score?.toString() || '-',
        suggestions[1]?.maleName || '-',
        suggestions[1]?.score?.toString() || '-',
        suggestions[2]?.maleName || '-',
        suggestions[2]?.score?.toString() || '-',
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    return csvContent;
  }

  /**
   * 統計情報を取得
   */
  async getStatistics(
    userId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    totalCount: number;
    successCount: number;
    errorCount: number;
    averageExecutionTime: number;
  }> {
    try {
      const histories = await this.getHistoriesForExport(userId, { startDate, endDate });

      const successHistories = histories.filter((h) => h.status === 'success');
      const totalExecutionTime = successHistories.reduce(
        (sum, h) => sum + h.executionTime,
        0
      );

      return {
        totalCount: histories.length,
        successCount: successHistories.length,
        errorCount: histories.filter((h) => h.status === 'error').length,
        averageExecutionTime:
          successHistories.length > 0
            ? totalExecutionTime / successHistories.length
            : 0,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`統計情報の取得に失敗しました: ${error.message}`);
      }
      throw new Error('統計情報の取得に失敗しました');
    }
  }

  /**
   * 今日の実行回数を取得
   */
  async getTodayCount(userId: string): Promise<number> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const stats = await this.getStatistics(userId, today);
      return stats.totalCount;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`今日の実行回数の取得に失敗しました: ${error.message}`);
      }
      throw new Error('今日の実行回数の取得に失敗しました');
    }
  }
}

// シングルトンインスタンス
export const aiAnalysisHistoryService = new AIAnalysisHistoryService();
